import { useState, useEffect } from 'react';
import { Activity, Sparkles, TrendingUp, Target, Users, Brain, CheckCircle2, AlertCircle, Clock, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AIActivity {
  id: string;
  agent_id: string;
  activity_type: string;
  title: string;
  description: string;
  status: 'in_progress' | 'completed' | 'failed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
  agent?: {
    name: string;
    type: string;
  };
}

interface Agent {
  id: string;
  name: string;
  type: string;
  status: string;
}

export default function AIActivityFeed() {
  const [isOpen, setIsOpen] = useState(false);
  const [activities, setActivities] = useState<AIActivity[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filter, setFilter] = useState<'all' | 'in_progress' | 'completed'>('all');

  useEffect(() => {
    loadAgents();
    loadActivities();

    const subscription = supabase
      .channel('ai_activities')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_activities' }, () => {
        loadActivities();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadAgents = async () => {
    
    const { data, error } = await supabase
      .from('ai_agents')
      .select('*')
      .order('name');

   

    if (error) {
      
      return;
    }

    if (data) {
      
      setAgents(data);
    }
  };

  const loadActivities = async () => {
    
    const { data, error } = await supabase
      .from('ai_activities')
      .select(`
        *,
        agent:ai_agents(name, type)
      `)
      .order('created_at', { ascending: false })
      .limit(50);

    

    if (error) {
      
      return;
    }

    if (data) {
      const formattedActivities = data.map(activity => ({
        ...activity,
        agent: Array.isArray(activity.agent) ? activity.agent[0] : activity.agent
      }));
      
      setActivities(formattedActivities);
    }
  };

  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    return activity.status === filter;
  });

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'analysis': return Brain;
      case 'prediction': return TrendingUp;
      case 'action': return Target;
      case 'insight': return Sparkles;
      case 'monitoring': return Activity;
      default: return Activity;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in_progress': return 'text-blue-600 bg-blue-50';
      case 'failed': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const activeAgentsCount = agents.filter(a => a.status === 'active' || a.status === 'processing').length;
  const inProgressCount = activities.filter(a => a.status === 'in_progress').length;

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-20 right-6 bg-white rounded-full shadow-lg border border-gray-200 px-4 py-2.5 flex items-center gap-2 hover:shadow-xl transition-all z-40 group"
      >
        <div className="relative">
          <Activity className="w-5 h-5 text-blue-600" />
          {inProgressCount > 0 && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-[10px] text-white font-bold">{inProgressCount}</span>
            </div>
          )}
        </div>
        <span className="font-medium text-gray-700">AI Activity</span>
        <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-full">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-xs font-medium text-blue-700">{activeAgentsCount} agents</span>
        </div>
      </button>

      {isOpen && (
        <div className="fixed top-20 right-6 w-[420px] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 max-h-[calc(100vh-7rem)] flex flex-col animate-in fade-in slide-in-from-right-4">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">AI Activity Feed</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          <div className="px-5 py-3 border-b border-gray-200 bg-white">
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('in_progress')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'in_progress'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                In Progress ({inProgressCount})
              </button>
              <button
                onClick={() => setFilter('completed')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'completed'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Completed
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredActivities.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {filteredActivities.map((activity) => {
                  const Icon = getActivityIcon(activity.activity_type);
                  return (
                    <div
                      key={activity.id}
                      className="px-5 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${getStatusColor(activity.status)}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-medium text-gray-900 text-sm">
                              {activity.title}
                            </h4>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getPriorityColor(activity.priority)}`}>
                              {activity.priority}
                            </span>
                          </div>
                          {activity.description && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {activity.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              {activity.agent?.name || 'AI Agent'}
                            </span>
                            <span className="w-1 h-1 bg-gray-300 rounded-full" />
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(activity.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <Activity className="w-12 h-12 text-gray-300 mb-3" />
                <h4 className="font-medium text-gray-900 mb-1">No activities yet</h4>
                <p className="text-sm text-gray-500">
                  AI agents will appear here as they work
                </p>
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>Live updates enabled</span>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium">{activeAgentsCount} agents active</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
